import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Coins, LogOut, Menu, X, Receipt, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { usePricesHidden } from "@/lib/priceVisibility";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notifs, setNotifs] = useState([]);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [pricesHidden, togglePrices] = usePricesHidden();

    const fetchNotifs = async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications");
            setNotifs(data);
        } catch {}
    };

    useEffect(() => {
        fetchNotifs();
        const t = setInterval(fetchNotifs, 15000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [user]);

    const unread = notifs.filter((n) => !n.read).length;

    const handleNotifClick = async (n) => {
        try { await api.patch(`/notifications/${n.id}/read`); } catch {}
        setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        if (n.link) navigate(n.link);
    };

    const markAllRead = async () => {
        try { await api.post("/notifications/read-all"); } catch {}
        setNotifs((prev) => prev.map((x) => ({ ...x, read: true })));
    };

    // Makelaars / MM accounts see "Tools" in their nav as soon as the admin
    // has flipped at least one tool on for them. Admin always sees it.
    const hasAnyTool = !!user && (
        user.role === "admin" ||
        Object.values(user.tool_access || {}).some(Boolean)
    );

    // Makelaars: hide "Agenda" until at least one shoot is scheduled and hide
    // "Recente projecten" until they have at least one project.
    const [navFlags, setNavFlags] = useState({ hasProjects: false, hasShoots: false });
    useEffect(() => {
        if (!user || user.role === "admin") return;
        (async () => {
            try {
                const [reqs, ag] = await Promise.all([
                    api.get("/requests"),
                    api.get("/availability/agenda"),
                ]);
                setNavFlags({
                    hasProjects: (reqs.data || []).length > 0,
                    hasShoots: (ag.data?.scheduled || []).length > 0,
                });
            } catch {}
        })();
    }, [user]);

    const navItems = user
        ? user.role === "admin"
            ? [
                  { to: "/admin", label: t("dashboard.title") },
                  { to: "/admin/calendar", label: t("navbar.agenda") },
                  { to: "/admin/users", label: t("navbar.users") },
                  { to: "/admin/tools", label: t("navbar.tools") },
                  { to: "/admin/mails", label: "Mails" },
                  { to: "/admin/leads", label: "Leads" },
                  { to: "/admin/stats", label: "Omzet" },
                  { to: "/admin/retention", label: "Retentie" },
                  { to: "/admin/visitors", label: "Bezoekers" },
                  { to: "/catalog", label: t("navbar.catalog") },
                  { to: "/admin/fileshare", label: "Fileshare" },
                  { to: "/admin/settings", label: t("navbar.settings") },
              ]
            : [
                  ...(navFlags.hasProjects ? [{ to: "/dashboard", label: user?.account_type === "marketing_manager" ? t("dashboard.all_projects") : t("dashboard.recent_projects") }] : []),
                  ...(navFlags.hasShoots ? [{ to: "/agenda", label: t("navbar.agenda") }] : []),                  ...(hasAnyTool ? [{ to: "/tools", label: t("navbar.tools") }] : []),
                  ...(user?.account_type === "photographer" ? [] : [
                      { to: "/catalog", label: t("navbar.catalog") },
                      { to: "/new-request", label: t("dashboard.new_request") },
                  ]),
              ]
        : [];

    return (
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200" data-testid="navbar">
            <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 h-20 sm:h-20 md:h-24 flex items-center justify-between">
                <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/"} className="flex items-center gap-2 sm:gap-3 group shrink-0" data-testid="nav-logo">
                    <img
                        src="https://customer-assets.emergentagent.com/job_makelaar-booking/artifacts/c00a1vjs_Logo%20Social%20Factory%20werkbestand_Tekengebied%201-02.png"
                        alt="Social Factory"
                        className="h-16 w-16 sm:h-16 sm:w-16 md:h-20 md:w-20 object-contain invert"
                    />
                    <div className="hidden sm:flex flex-col leading-none">
                        <span className="text-base font-bold tracking-tight text-zinc-950">{t("common.brand_studio")}</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end
                            data-testid={`nav-link-${item.to.replace(/\//g, "-")}`}
                            className={({ isActive }) =>
                                `px-3 py-1.5 text-sm rounded-sm transition-colors ${
                                    isActive ? "text-[#E5A93D] bg-zinc-100" : "text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {user ? (
                        <>
                            {hasAnyTool && user.role !== "admin" && (
                                <Link
                                    to="/tools#bijtanken"
                                    data-testid="nav-credits-badge"
                                    title="Mijn credits — klik om bij te tanken"
                                    className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-[#22c55e]/30 bg-[#22c55e]/[0.06] hover:bg-[#22c55e]/[0.12] hover:border-[#22c55e]/50 transition-colors group"
                                >
                                    <Coins className="h-4 w-4 text-[#22c55e]" />
                                    <span className="text-sm font-semibold tabular-nums text-[#22c55e]" data-testid="nav-credits-balance">
                                        {user.credits_balance ?? 0}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 group-hover:text-zinc-600 hidden md:inline">{t("navbar.credits_balance")}</span>
                                </Link>
                            )}
                            {user.role === "admin" && (
                                <button
                                    onClick={togglePrices}
                                    className={`h-9 w-9 rounded-sm border flex items-center justify-center transition-colors ${pricesHidden ? "border-[#E5A93D] bg-[#E5A93D]/10 text-[#E5A93D]" : "border-zinc-200 hover:border-zinc-400 text-zinc-600"}`}
                                    title={pricesHidden ? "Prijzen tonen" : "Prijzen verbergen (schermopname)"}
                                    data-testid="global-price-toggle"
                                >
                                    {pricesHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            )}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="relative h-9 w-9 rounded-sm border border-zinc-200 hover:border-zinc-400 flex items-center justify-center transition-colors" data-testid="notif-trigger">
                                        <Bell className="h-4 w-4 text-zinc-600" />
                                        {unread > 0 && (
                                            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-[#E5A93D] text-black text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-count">{unread}</span>
                                        )}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-80 bg-[#FFFFFF] border-zinc-200">
                                    <DropdownMenuLabel className="flex items-center justify-between">
                                        <span>Meldingen</span>
                                        {unread > 0 && (
                                            <button onClick={markAllRead} className="text-xs text-[#E5A93D] hover:underline" data-testid="notif-mark-all-read">Alles gelezen</button>
                                        )}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator className="bg-zinc-100" />
                                    <div className="max-h-96 overflow-auto scrollbar-thin">
                                        {notifs.length === 0 && <div className="p-4 text-sm text-zinc-500 text-center">Nog geen meldingen</div>}
                                        {notifs.map((n) => (
                                            <DropdownMenuItem
                                                key={n.id}
                                                onClick={() => handleNotifClick(n)}
                                                className="flex flex-col items-start gap-1 cursor-pointer py-3 focus:bg-zinc-100"
                                                data-testid={`notif-item-${n.id}`}
                                            >
                                                <div className="flex items-center gap-2 w-full">
                                                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#E5A93D]" />}
                                                    <span className="text-sm font-medium text-zinc-950 flex-1">{n.title}</span>
                                                </div>
                                                <span className="text-xs text-zinc-500">{n.message}</span>
                                            </DropdownMenuItem>
                                        ))}
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-2 h-9 px-2 sm:px-3 rounded-sm border border-zinc-200 hover:border-zinc-400 transition-colors" data-testid="user-menu-trigger">
                                        <div className="h-6 w-6 rounded-full bg-[#E5A93D] text-black flex items-center justify-center text-xs font-bold">
                                            {user.name?.[0]?.toUpperCase() || "U"}
                                        </div>
                                        <span className="hidden sm:inline text-sm text-zinc-800">{user.name}</span>
                                        {user.role === "admin" && (
                                            <Badge className="hidden sm:inline-flex bg-[#E5A93D] text-black text-[10px] px-1.5 py-0 h-4">ADMIN</Badge>
                                        )}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-[#FFFFFF] border-zinc-200">
                                    <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                                    <DropdownMenuSeparator className="bg-zinc-100" />
                                    <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer focus:bg-zinc-100" data-testid="profile-link">
                                        {t("navbar.profile")}
                                    </DropdownMenuItem>
                                    {hasAnyTool && user.role !== "admin" && (
                                        <DropdownMenuItem onClick={() => navigate("/tools/invoices")} className="cursor-pointer focus:bg-zinc-100" data-testid="nav-my-invoices-link">
                                            <Receipt className="mr-2 h-4 w-4" />
                                            {t("navbar.my_invoices")}
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator className="bg-zinc-100" />
                                    <div className="px-2 py-2 flex items-center justify-between gap-3">
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t("language.label")}</span>
                                        <LanguageSwitcher variant="compact" />
                                    </div>
                                    <DropdownMenuSeparator className="bg-zinc-100" />
                                    <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer focus:bg-zinc-100" data-testid="logout-btn">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        {t("navbar.logout")}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <button className="md:hidden h-9 w-9 rounded-sm border border-zinc-200 flex items-center justify-center" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-trigger">
                                {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                            </button>
                        </>
                    ) : (
                        <div className="flex items-center gap-1.5 sm:gap-2">
                            <LanguageSwitcher variant="compact" />
                            <Link to="/login">
                                <Button variant="ghost" className="h-9 px-2 sm:px-4 text-xs sm:text-sm text-zinc-800 hover:bg-zinc-100 rounded-sm" data-testid="login-link-btn">{t("navbar.login")}</Button>
                            </Link>
                            <Link to="/register">
                                <Button className="h-9 px-2.5 sm:px-4 text-xs sm:text-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="register-link-btn">{t("navbar.register")}</Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {mobileOpen && user && (
                <div className="md:hidden border-t border-zinc-200 bg-white/95 backdrop-blur-xl">
                    <nav className="flex flex-col p-4 gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end
                                onClick={() => setMobileOpen(false)}
                                data-testid={`mobile-nav-${item.to.replace(/\//g, "-")}`}
                                className={({ isActive }) =>
                                    `px-3 py-2 text-sm rounded-sm ${isActive ? "text-[#E5A93D] bg-zinc-100" : "text-zinc-600"}`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <button
                            onClick={() => { setMobileOpen(false); navigate("/profile"); }}
                            className="text-left px-3 py-2 text-sm rounded-sm text-zinc-600"
                            data-testid="mobile-profile-btn"
                        >
                            {t("navbar.profile")}
                        </button>
                        {hasAnyTool && user.role !== "admin" && (
                            <button
                                onClick={() => { setMobileOpen(false); navigate("/tools/invoices"); }}
                                className="flex items-center gap-2 text-left px-3 py-2 text-sm rounded-sm text-zinc-600"
                                data-testid="mobile-my-invoices-btn"
                            >
                                <Receipt className="h-4 w-4" />
                                {t("navbar.my_invoices")}
                            </button>
                        )}
                        <div className="px-3 py-2 flex items-center justify-between gap-3 mt-2 border-t border-zinc-200 pt-3">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t("language.label")}</span>
                            <LanguageSwitcher variant="compact" />
                        </div>
                        <button
                            onClick={() => { setMobileOpen(false); logout(); navigate("/"); }}
                            className="flex items-center gap-2 text-left px-3 py-2 text-sm rounded-sm text-red-600 hover:bg-zinc-100 mt-2"
                            data-testid="mobile-logout-btn"
                        >
                            <LogOut className="h-4 w-4" />
                            {t("navbar.logout")}
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
}
